import React, {useEffect, useState} from "react";
import {Input} from "./input";
import {Button} from "./Button";
import TextArea from "./TextArea";
import api from "../api/axiosInstance";

export default function BundledProductFormModal({open, onClose, onSubmit, bundle}) {
    const [images, setImages] = useState([]);
    const [alttext, setAlttext] = useState("");
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [details, setDetails] = useState("");
    const [isFeatured, setIsFeatured] = useState(false);
    const [price, setPrice] = useState("");
    const [tags, setTags] = useState("");
    const [newProduct, setNewProduct] = useState(false);
    const [products, setProducts] = useState([]);
    const [availableProducts, setAvailableProducts] = useState([]);
    const [priority, setPriority] = useState(undefined);
    const [categorizations, setCategorizations] = useState([]);

    useEffect(() => {
        fetchAvailableProducts();
    }, []);

    useEffect(() => {
        if (bundle) {
            setImages(bundle.image || []);
            setAlttext(bundle.alttext || "");
            setTitle(bundle.title || "");
            setDescription(bundle.description || "");
            setDetails((bundle.details || []).join(", "));
            setIsFeatured(bundle.isFeatured || false);
            setPrice(bundle.price != null ? String(bundle.price) : "");
            setTags((bundle.tags || []).join(", "));
            setNewProduct(bundle.newProduct || false);
            setProducts(bundle.products || []);
            setPriority(bundle.priority ?? undefined);
            setCategorizations(bundle.categorizations || []);
        } else {
            setImages([]);
            setAlttext("");
            setTitle("");
            setDescription("");
            setDetails("");
            setIsFeatured(false);
            setPrice("");
            setTags("");
            setNewProduct(false);
            setProducts([]);
            setPriority(undefined);
            setCategorizations([]);
        }
    }, [bundle, open]);

    const fetchAvailableProducts = async () => {
        const res = await api.get("/admin/products?page=1&limit=1000");
        setAvailableProducts(res.data.products || []);
    };

    const handleAddImage = () => setImages([...images, ""]);
    const handleImageChange = (index, value) => {
        const updated = [...images];
        updated[index] = value;
        setImages(updated);
    };
    const handleRemoveImage = (index) => setImages(images.filter((_, i) => i !== index));

    const handleToggleProduct = (sku) => {
        const exists = products.find(p => p.sku === sku);
        if (exists) {
            setProducts(products.filter(p => p.sku !== sku));
        } else {
            setProducts([...products, {t: "", sku}]);
        }
    };

    const handleTitleChange = (sku, value) => {
        setProducts(products.map(p => p.sku === sku ? {...p, t: value} : p));
    };

    const handleAddCategorization = () => {
        setCategorizations([...categorizations, {category: "", subcategory: ""}]);
    };

    const handleCategorizationChange = (index, field, value) => {
        const updated = [...categorizations];
        updated[index][field] = value;
        setCategorizations(updated);
    };

    const handleRemoveCategorization = (index) => {
        setCategorizations(categorizations.filter((_, i) => i !== index));
    };

    const handleSubmit = () => {
        onSubmit({
            image: images.filter(Boolean),
            alttext,
            title,
            description,
            details: details.split(",").map(d => d.trim()).filter(Boolean),
            isFeatured,
            price: parseFloat(price),
            tags: tags.split(",").map(t => t.trim()).filter(Boolean),
            newProduct,
            priority,
            categorizations: categorizations.filter(c => c.category && c.subcategory),
            products,
        });
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex justify-center items-center p-4">
            <div className="bg-white rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-auto p-6 space-y-6">
                <h2 className="text-2xl font-bold">{bundle ? "Edit Bundled Product" : "Add Bundled Product"}</h2>

                <div>
                    <h3 className="text-lg font-semibold mb-2">Product Images</h3>
                    {images.map((img, index) => (
                        <div key={index} className="flex gap-2 mb-2">
                            <Input
                                placeholder="Image URL"
                                value={img}
                                onChange={(e) => handleImageChange(index, e.target.value)}
                                className="flex-1"
                            />
                            <Button variant="outline" onClick={() => handleRemoveImage(index)}>Remove</Button>
                        </div>
                    ))}
                    <Button variant="outline" onClick={handleAddImage}>+ Add Image</Button>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <Input name="alttext" placeholder="Alt Text" value={alttext}
                           onChange={e => setAlttext(e.target.value)}/>
                    <Input name="title" placeholder="Title" value={title} onChange={e => setTitle(e.target.value)}
                           className="col-span-2"/>
                    <TextArea name="description" placeholder="Description" value={description}
                              onChange={e => setDescription(e.target.value)} className="col-span-2"/>
                    <Input name="details" placeholder="Details (comma separated)" value={details}
                           onChange={e => setDetails(e.target.value)} className="col-span-2"/>
                    <Input name="priority" type="number" placeholder="Priority" value={priority ?? ""}
                           onChange={e => setPriority(e.target.value ? parseInt(e.target.value) : undefined)}/>
                    <Input name="tags" placeholder="Tags (comma separated)" value={tags}
                           onChange={e => setTags(e.target.value)}/>
                </div>

                <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2">
                        <input type="checkbox" checked={isFeatured} onChange={e => setIsFeatured(e.target.checked)}/>
                        Featured
                    </label>
                    <label className="flex items-center gap-2">
                        <input type="checkbox" checked={newProduct} onChange={e => setNewProduct(e.target.checked)}/>
                        New Product
                    </label>
                </div>

                <div>
                    <h3 className="text-lg font-semibold mb-2">Categorizations</h3>
                    {categorizations.map((c, index) => (
                        <div key={index} className="flex gap-2 mb-2">
                            <Input placeholder="Category" value={c.category}
                                   onChange={e => handleCategorizationChange(index, "category", e.target.value)}/>
                            <Input placeholder="Subcategory" value={c.subcategory}
                                   onChange={e => handleCategorizationChange(index, "subcategory", e.target.value)}/>
                            <Button variant="outline" onClick={() => handleRemoveCategorization(index)}>Remove</Button>
                        </div>
                    ))}
                    <Button variant="outline" onClick={handleAddCategorization}>+ Add Categorization</Button>
                </div>

                <Input placeholder="price" value={price}
                       onChange={e => setPrice(e.target.value)}/>

                <div>
                    <h3 className="text-lg font-semibold mb-2">Select Products</h3>
                    <div className="grid grid-cols-2 gap-2 max-h-64 overflow-auto border rounded p-2">
                        {availableProducts.map(p => {
                            const selected = products.find(bp => bp.sku === p.sku);
                            return (
                                <div key={p.sku} className="flex items-center gap-2">
                                    <input type="checkbox" checked={!!selected}
                                           onChange={() => handleToggleProduct(p.sku)}/>
                                    <span>{p.title} ({p.sku})</span>
                                    {selected && (
                                        <Input placeholder="Title snippet (t)" value={selected.t}
                                               onChange={(e) => handleTitleChange(p.sku, e.target.value)}
                                               className="flex-1"/>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="flex justify-end gap-4 pt-4">
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={handleSubmit}>{bundle ? "Update" : "Add"} Bundle</Button>
                </div>
            </div>
        </div>
    );
}
